import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Box, Group, Menu, Text, TextInput } from '@mantine/core';
import { Check, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';

// Channel-group labels created by our provider pipelines follow the
// convention "<Provider> • <Category>" (e.g. "A1 • Спорт"). Splitting on that
// separator is enough to build a Provider -> Category tree without any backend
// changes: the provider name IS the group-title prefix.
const BULLET_RE = /^(.*?)\s*•\s*(.*)$/;

function buildProviderTree(options) {
  const pinned = [];
  const byProvider = new Map();
  const ungrouped = [];

  for (const opt of options) {
    if (opt.value === 'all') {
      pinned.push(opt);
      continue;
    }
    const m = opt.label.match(BULLET_RE);
    if (m && m[1] && m[2]) {
      if (!byProvider.has(m[1])) byProvider.set(m[1], []);
      byProvider.get(m[1]).push({ value: opt.value, label: m[2] });
    } else {
      ungrouped.push(opt);
    }
  }

  const providers = Array.from(byProvider.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([provider, categories]) => ({
      provider,
      categories: categories.sort((a, b) => a.label.localeCompare(b.label)),
    }));

  ungrouped.sort((a, b) => a.label.localeCompare(b.label));
  return { pinned, providers, ungrouped };
}

// Touch devices have no hover, so the desktop flyout submenu is unusable
// there: the first tap opens it via a synthesised mouseover, and the next
// touchmove -- i.e. the moment the user starts scrolling -- fires mouseout and
// closes it again. Those devices get a drill-down inside the single dropdown.
function useCoarsePointer() {
  const query = '(any-hover: hover)';
  const [coarse, setCoarse] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return !window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (event) => setCoarse(!event.matches);
    mq.addEventListener?.('change', onChange);

    // Hybrid laptops mis-report themselves, so let real input decide.
    const onPointer = (event) => {
      if (event.pointerType === 'mouse') setCoarse(false);
      else if (event.pointerType === 'touch') setCoarse(true);
    };
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerdown', onPointer, { passive: true });

    return () => {
      mq.removeEventListener?.('change', onChange);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, []);
  return coarse;
}

// Two-level "Filter by group" control: top level = provider, second level =
// that provider's categories (a side flyout with a mouse, a drill-down on
// touch). Drop-in replacement for the flat Mantine <Select> that used to list
// every "Provider • Category" combination as one long alphabetical list.
//
// DISPATCHARR_GROUP_MENU_SUB_R95: the flyout is Mantine's own Menu.Sub. The
// previous version nested a second <Menu> inside the first, and that inner
// menu rendered its dropdown in its own portal -- outside the root menu's
// dropdown, so the root's click-outside detector (which fires on mousedown)
// treated a press inside the flyout as a click elsewhere and closed
// everything before the category's click could land. A firm touchpad click
// happened to survive that race on Edge, a tap did not. Menu.Sub renders the
// flyout inside the root dropdown's DOM, so there is no outside to click.
export default function ProviderGroupMenu({
  options,
  value,
  onChange,
  clearable = true,
  placeholder = 'Filter by group',
  width = 220,
}) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [drillProvider, setDrillProvider] = useState(null);
  // DISPATCHARR_GROUP_MENU_TAP_R40: some trackpads report a tap as
  // pointerType "touch", which would flip the layout between flyout and
  // drill-down while the user is mid-click. Settle the mode when the menu
  // opens and leave it alone until it closes again.
  const livePointerCoarse = useCoarsePointer();
  const [coarse, setCoarse] = useState(livePointerCoarse);
  useEffect(() => {
    if (!opened) setCoarse(livePointerCoarse);
  }, [livePointerCoarse, opened]);

  const { pinned, providers, ungrouped } = useMemo(
    () => buildProviderTree(options),
    [options]
  );

  const selectedLabel = useMemo(() => {
    const found = options.find((o) => String(o.value) === String(value));
    return found ? found.label : null;
  }, [options, value]);

  const resetTransient = () => {
    setSearch('');
    setDrillProvider(null);
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpened(false);
    resetTransient();
  };

  const q = search.trim().toLowerCase();
  const matches = (text) => text.toLowerCase().includes(q);

  const filteredProviders = q
    ? providers
        .map((p) => ({
          ...p,
          categories: p.categories.filter(
            (c) => matches(c.label) || matches(p.provider)
          ),
        }))
        .filter((p) => p.categories.length > 0)
    : providers;
  const filteredUngrouped = q
    ? ungrouped.filter((o) => matches(o.label))
    : ungrouped;

  const isSelected = (val) => String(value) === String(val);

  const renderLeaf = (opt, prefix) => (
    <Menu.Item
      key={opt.value}
      onClick={() => handleSelect(opt.value)}
      leftSection={
        isSelected(opt.value) ? (
          <Check size={14} />
        ) : (
          <span style={{ width: 14, display: 'inline-block' }} />
        )
      }
      py={coarse ? 10 : undefined}
    >
      {prefix ? prefix + ' • ' + opt.label : opt.label}
    </Menu.Item>
  );

  // While searching, both modes show flat matches so a category can be reached
  // without walking into its provider first.
  const searchResults = () => (
    <>
      {filteredProviders.map(({ provider, categories }) =>
        categories.map((cat) => renderLeaf(cat, provider))
      )}
      {filteredUngrouped.map((opt) => renderLeaf(opt))}
      {filteredProviders.length === 0 && filteredUngrouped.length === 0 && (
        <Text size="xs" c="dimmed" p="sm">
          No matches
        </Text>
      )}
    </>
  );

  const drilled = drillProvider
    ? filteredProviders.find((p) => p.provider === drillProvider)
    : null;

  const ungroupedSection = () =>
    filteredUngrouped.length > 0 && (
      <>
        <Menu.Divider />
        <Menu.Label>Other</Menu.Label>
        {filteredUngrouped.map((opt) => renderLeaf(opt))}
      </>
    );

  const body = () => {
    if (q) return searchResults();

    // Touch: drill-down inside this one dropdown -- no hover, no nested
    // portal, one scroll container, so scrolling never dismisses the menu.
    if (coarse) {
      if (drilled) {
        return (
          <>
            <Menu.Item
              leftSection={<ChevronLeft size={14} />}
              onClick={() => setDrillProvider(null)}
              py={10}
              fw={600}
            >
              {drilled.provider}
            </Menu.Item>
            <Menu.Divider />
            {drilled.categories.map((cat) => renderLeaf(cat))}
          </>
        );
      }
      return (
        <>
          {pinned.map((opt) => renderLeaf(opt))}
          {pinned.length > 0 && <Menu.Divider />}
          {filteredProviders.map(({ provider, categories }) => (
            <Menu.Item
              key={provider}
              rightSection={<ChevronRight size={14} />}
              onClick={() => setDrillProvider(provider)}
              py={10}
              fw={categories.some((c) => isSelected(c.value)) ? 700 : 400}
            >
              {provider}
            </Menu.Item>
          ))}
          {ungroupedSection()}
        </>
      );
    }

    // Pointer devices: the side flyout, as Mantine's Menu.Sub.
    return (
      <>
        {pinned.map((opt) => renderLeaf(opt))}
        {pinned.length > 0 && <Menu.Divider />}
        {filteredProviders.map(({ provider, categories }) => (
          <Menu.Sub
            key={provider}
            position="right-start"
            offset={0}
            /* A short grace so a diagonal move from the item into the
               flyout does not close it on the way. */
            closeDelay={200}
            width={240}
            shadow="md"
          >
            <Menu.Sub.Target>
              <Menu.Sub.Item
                fw={categories.some((c) => isSelected(c.value)) ? 700 : 400}
              >
                {provider}
              </Menu.Sub.Item>
            </Menu.Sub.Target>
            <Menu.Sub.Dropdown>
              <Box style={{ maxHeight: 420, overflowY: 'auto' }}>
                {categories.map((cat) => renderLeaf(cat))}
              </Box>
            </Menu.Sub.Dropdown>
          </Menu.Sub>
        ))}
        {ungroupedSection()}
      </>
    );
  };

  return (
    <Menu
      opened={opened}
      onChange={(next) => {
        setOpened(next);
        if (!next) resetTransient();
      }}
      position="bottom-start"
      shadow="md"
      width={coarse ? 300 : 260}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <TextInput
          readOnly
          value={selectedLabel || ''}
          placeholder={placeholder}
          w={width}
          style={{ cursor: 'pointer' }}
          rightSection={
            <Group gap={2} wrap="nowrap" pr={4}>
              {clearable && value && value !== 'all' && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect('all');
                  }}
                  aria-label="Clear group filter"
                >
                  <X size={12} />
                </ActionIcon>
              )}
              <ChevronDown size={14} style={{ opacity: 0.6 }} />
            </Group>
          }
          rightSectionWidth={clearable && value && value !== 'all' ? 46 : 28}
        />
      </Menu.Target>

      <Menu.Dropdown>
        <Box p={4} pb={6}>
          <TextInput
            placeholder="Search providers/categories..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
            onClick={(e) => e.stopPropagation()}
          />
        </Box>

        {coarse || q ? (
          // The drill-down and the search list can be long; they scroll.
          <Box
            style={{
              maxHeight: coarse ? 'min(60vh, 420px)' : 420,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            {body()}
          </Box>
        ) : (
          // The flyout renders inside this dropdown, so no overflow container
          // may wrap it -- it would be clipped at the dropdown's edge. The
          // provider list itself is a handful of rows.
          <Box>{body()}</Box>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
